import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clamp,
  hexToHsl,
  hslToHex,
  normalizeHex,
  type Hsl,
} from './themeColorUtils';
import { cn } from '@/lib/utils';
import {
  colorWheelHandle,
  colorWheelHandleRing,
  colorWheelHandleSl,
  colorWheelHexInput,
  colorWheelHexLabel,
  colorWheelHexRow,
  colorWheelPreview,
  colorWheelRing,
  colorWheelRingHole,
  colorWheelRoot,
  colorWheelSl,
  colorWheelWheel,
} from './theme-color-layout';

const WHEEL_SIZE = 168;
const WHEEL_CENTER = WHEEL_SIZE / 2;
const WHEEL_OUTER_R = WHEEL_CENTER - 4;
const WHEEL_INNER_R = WHEEL_OUTER_R - 18;
const SL_SIZE = WHEEL_INNER_R * 2 - 8;

type DragTarget = 'ring' | 'sl' | null;

type ColorWheelPickerProps = {
  value: string;
  onChange: (hex: string) => void;
};

const hueFromPoint = (x: number, y: number): number => {
  const dx = x - WHEEL_CENTER;
  const dy = y - WHEEL_CENTER;
  const angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
  return (angle + 360) % 360;
};

const slFromPoint = (x: number, y: number): Pick<Hsl, 's' | 'l'> => {
  const offset = (WHEEL_SIZE - SL_SIZE) / 2;
  const localX = clamp(x - offset, 0, SL_SIZE);
  const localY = clamp(y - offset, 0, SL_SIZE);
  return {
    s: (localX / SL_SIZE) * 100,
    l: 100 - (localY / SL_SIZE) * 100,
  };
};

const ColorWheelPicker: React.FC<ColorWheelPickerProps> = ({
  value,
  onChange,
}) => {
  const wheelRef = useRef<HTMLDivElement>(null);
  const [dragTarget, setDragTarget] = useState<DragTarget>(null);
  const [draftHsl, setDraftHsl] = useState<Hsl | null>(null);
  const [hexInput, setHexInput] = useState(value);

  const hsl = draftHsl ?? hexToHsl(value);
  const previewHex = hslToHex(hsl.h, hsl.s, hsl.l);

  useEffect(() => {
    if (!dragTarget) {
      setHexInput(value);
    }
  }, [value, dragTarget]);

  const applyHsl = useCallback(
    (next: Hsl) => {
      const clamped: Hsl = {
        h: (next.h + 360) % 360,
        s: clamp(next.s, 0, 100),
        l: clamp(next.l, 0, 100),
      };
      setDraftHsl(clamped);
      const hex = hslToHex(clamped.h, clamped.s, clamped.l);
      setHexInput(hex);
      onChange(hex);
    },
    [onChange],
  );

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number, target: DragTarget) => {
      const rect = wheelRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = clientX - rect.left;
      const y = clientY - rect.top;

      if (target === 'ring') {
        applyHsl({ ...hsl, h: hueFromPoint(x, y) });
        return;
      }

      if (target === 'sl') {
        const { s, l } = slFromPoint(x, y);
        applyHsl({ ...hsl, s, l });
      }
    },
    [applyHsl, hsl],
  );

  const handleRingPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragTarget('ring');
    updateFromPointer(e.clientX, e.clientY, 'ring');
  };

  const handleSlPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragTarget('sl');
    updateFromPointer(e.clientX, e.clientY, 'sl');
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragTarget) return;
    updateFromPointer(e.clientX, e.clientY, dragTarget);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragTarget) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragTarget(null);
    setDraftHsl(null);
  };

  const hueRad = (hsl.h * Math.PI) / 180;
  const ringHandleX = WHEEL_CENTER + Math.sin(hueRad) * ((WHEEL_INNER_R + WHEEL_OUTER_R) / 2);
  const ringHandleY = WHEEL_CENTER - Math.cos(hueRad) * ((WHEEL_INNER_R + WHEEL_OUTER_R) / 2);

  const slOffset = (WHEEL_SIZE - SL_SIZE) / 2;
  const slHandleX = slOffset + (hsl.s / 100) * SL_SIZE;
  const slHandleY = slOffset + (1 - hsl.l / 100) * SL_SIZE;

  const handleHexCommit = () => {
    const normalized = normalizeHex(hexInput);
    if (normalized) {
      onChange(normalized);
      setHexInput(normalized);
    } else {
      setHexInput(value);
    }
  };

  return (
    <div className={colorWheelRoot}>
      <div
        ref={wheelRef}
        className={colorWheelWheel}
        style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}>
        <div
          className={colorWheelRing}
          onPointerDown={handleRingPointerDown}
          role="slider"
          aria-label="Hue"
          aria-valuemin={0}
          aria-valuemax={360}
          aria-valuenow={Math.round(hsl.h)}>
          <span className={colorWheelRingHole} aria-hidden="true" />
        </div>
        <div
          className={colorWheelSl}
          style={{
            width: SL_SIZE,
            height: SL_SIZE,
            left: slOffset,
            top: slOffset,
            backgroundColor: `hsl(${hsl.h}, 100%, 50%)`,
          }}
          onPointerDown={handleSlPointerDown}
          role="slider"
          aria-label="Saturation and lightness"
          aria-valuetext={`${Math.round(hsl.s)}% saturation, ${Math.round(hsl.l)}% lightness`}
        />
        <span
          className={cn(colorWheelHandle, colorWheelHandleRing)}
          style={{ left: ringHandleX, top: ringHandleY }}
          aria-hidden="true"
        />
        <span
          className={cn(colorWheelHandle, colorWheelHandleSl)}
          style={{
            left: slHandleX,
            top: slHandleY,
            backgroundColor: previewHex,
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default ColorWheelPicker;
